import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { 
  getAuth, 
  GoogleAuthProvider, 
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  User as FirebaseUser
} from 'firebase/auth';
import { getSessionToken } from './sessionToken';
import { isInAppBrowser } from './inAppBrowser';
import { stopGoogleAutoSelect } from './googleAutoSelect';

// Firebase configuration - from Firebase Console
const firebaseConfig = {
  apiKey: "AIzaSyD-G_qE6gjWRepHkaHWzb_uULrBDEYLvpk",
  authDomain: "vivideo-ai.firebaseapp.com",
  projectId: "vivideo-ai",
  storageBucket: "vivideo-ai.firebasestorage.app",
  messagingSenderId: "989845562420",
  appId: "1:989845562420:web:5c4058977636a6490e9661",
  measurementId: "G-6BTP7XQEHX"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const auth = getAuth(app);

// Google OAuth Client ID for One Tap sign-in (Web client type)
// Found in google-services.json with client_type: 3
export const GOOGLE_CLIENT_ID = '989845562420-gihvqkjk4msohe18jkukfiak0cg7926j.apps.googleusercontent.com';

// Auth providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

// Microsoft OAuth provider
const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({
  prompt: 'select_account'
});
microsoftProvider.addScope('email');
microsoftProvider.addScope('profile');

// Magic link storage key
const MAGIC_LINK_EMAIL_KEY = 'vivideo_magic_link_email';

// When a popup can't be used we fall back to a full-page redirect. We stash the
// provider method here so that, after Firebase processes the redirect and
// onAuthStateChanged fires on return, AuthContext knows this was a fresh
// interactive sign-in (and which method) — so it can fire sign_up/login.
export const REDIRECT_METHOD_KEY = 'vivideo_redirect_method';
export type SignInMethod = 'google' | 'apple' | 'microsoft' | 'email';

/**
 * Sign in with an OAuth provider, using a popup by default but transparently
 * falling back to a full-page redirect when a popup isn't viable (in-app
 * webview, or the browser blocked the popup). Returns the user for the popup
 * path; returns null when a redirect was started (the page will navigate away
 * and the sign-in completes via onAuthStateChanged on return).
 */
async function signInWithProvider(
  provider: GoogleAuthProvider | OAuthProvider,
  method: SignInMethod,
): Promise<FirebaseUser | null> {
  if (isInAppBrowser()) {
    sessionStorage.setItem(REDIRECT_METHOD_KEY, method);
    await signInWithRedirect(auth, provider);
    return null;
  }
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    const code = (error as { code?: string })?.code;
    // Popup was blocked or the environment doesn't support popups → redirect.
    if (
      code === 'auth/popup-blocked' ||
      code === 'auth/operation-not-supported-in-this-environment'
    ) {
      sessionStorage.setItem(REDIRECT_METHOD_KEY, method);
      await signInWithRedirect(auth, provider);
      return null;
    }
    throw error;
  }
}

// Action code settings for magic link
const getActionCodeSettings = () => ({
  // URL to redirect back to after email link click
  url: window.location.origin + '/login',
  handleCodeInApp: true,
});

// Sign in functions
export async function signInWithGoogle(): Promise<FirebaseUser | null> {
  return signInWithProvider(googleProvider, 'google');
}

// Sign in with Google One Tap using ID token
export async function signInWithOneTap(idToken: string): Promise<FirebaseUser> {
  const credential = GoogleAuthProvider.credential(idToken);
  const result = await signInWithCredential(auth, credential);
  return result.user;
}

export async function signInWithApple(): Promise<FirebaseUser | null> {
  return signInWithProvider(appleProvider, 'apple');
}

// Sign in with Microsoft OAuth
export async function signInWithMicrosoft(): Promise<FirebaseUser | null> {
  return signInWithProvider(microsoftProvider, 'microsoft');
}

// Send Login Link to email for passwordless sign-in
export async function sendMagicLink(email: string): Promise<void> {
  await sendSignInLinkToEmail(auth, email, getActionCodeSettings());
  // Store email locally to complete sign-in after redirect
  localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
}

// Check if current URL is a magic link
export function isMagicLink(url: string = window.location.href): boolean {
  return isSignInWithEmailLink(auth, url);
}

// Complete magic link sign-in
export async function completeMagicLinkSignIn(email?: string, url: string = window.location.href): Promise<FirebaseUser> {
  // Get email from parameter or localStorage
  const signInEmail = email || localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
  
  if (!signInEmail) {
    throw new Error('Email is required to complete magic link sign-in');
  }
  
  const result = await signInWithEmailLink(auth, signInEmail, url);
  
  // Clear stored email after successful sign-in
  localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
  
  return result.user;
}

// Get stored magic link email (for showing in UI after redirect)
export function getMagicLinkEmail(): string | null {
  return localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
}

export async function signOut(): Promise<void> {
  // Google One Tap's auto sign-in goes off with the session (googleAutoSelect.ts), so /login does not sign it back in
  await Promise.all([firebaseSignOut(auth), stopGoogleAutoSelect()]);
}

/**
 * Complete any pending redirect-based OAuth sign-in. Must be called once on app
 * load so Firebase can process the provider callback URL before onAuthStateChanged
 * fires. Returns the signed-in user when a redirect just completed, else null.
 */
export async function completeRedirectSignIn(): Promise<FirebaseUser | null> {
  try {
    const result = await getRedirectResult(auth);
    return result?.user ?? null;
  } catch (error) {
    console.error('Redirect sign-in error:', error);
    sessionStorage.removeItem(REDIRECT_METHOD_KEY);
    throw error;
  }
}

export function onAuthChange(callback: (user: FirebaseUser | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

export function getCurrentUser(): FirebaseUser | null {
  return auth.currentUser;
}

// The backend Authorization: Bearer credential: the Firebase ID token, or for
// email-OTP sign-ins the backend-signed session token. Null when logged out.
export async function getIdToken(): Promise<string | null> {
  // Until Firebase restores a persisted sign-in, currentUser is null; wait for it.
  try { await auth.authStateReady(); } catch { /* fall through */ }
  const user = auth.currentUser;
  if (!user) return getSessionToken();
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

export { auth, app, analytics };
export type { FirebaseUser };
